import { images } from "services/api";
import { imageCache, isSignedImageUrlUsable } from "utils/imageCache";
import defaultFlowImage from "assets/Images/defaultFlowImage.png";

const inFlightSignedUrlRequests = new Map<string, Promise<string>>();
export const DEFAULT_FLOW_IMAGE_URL = defaultFlowImage;
export { isSignedImageUrlUsable };

export const isSignedImageUrl = (url: string): boolean =>
  url.startsWith("http") && (url.includes("X-Amz-Signature") || url.includes("Signature="));

export const extractImageKey = (rawUrl: string): string => {
  let key = rawUrl;

  if (!key) {
    return "";
  }

  try {
    if (key.startsWith("http")) {
      const url = new URL(key);
      const parts = url.pathname.split("/").filter(Boolean);
      return parts.join("/");
    }
  } catch (error) {
    // Ignore URL parsing errors and fall back to raw input.
  }

  return key;
};

export const resolveSignedImageUrl = async (rawUrl: string): Promise<string> => {
  const value = rawUrl?.trim();
  if (!value) {
    throw new Error("Invalid image URL");
  }

  // Legacy placeholder values map to the current static fallback.
  if (value === "default.png" || value === "/default.png") {
    return DEFAULT_FLOW_IMAGE_URL;
  }

  // Frontend-bundled/public assets should be loaded directly.
  if (
    value.startsWith("/static/") ||
    value.startsWith("/assets/") ||
    value.startsWith("./") ||
    value.startsWith("../") ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  const baseUrl = value.split("?")[0] ?? value;
  const key = extractImageKey(baseUrl);
  if (!key) {
    throw new Error("Invalid image key");
  }

  // A supplied signature belongs to the current content revision. A cache
  // entry for the same object key must not replace it with an older revision.
  if (isSignedImageUrl(value) && isSignedImageUrlUsable(value)) {
    imageCache.set(key, value);
    return value;
  }

  const cachedUrl = imageCache.get(key);
  if (cachedUrl) {
    return cachedUrl;
  }

  const existingRequest = inFlightSignedUrlRequests.get(key);
  if (existingRequest) {
    return existingRequest;
  }

  const request = images
    .getSignedUrl(key)
    .then((signedUrl) => {
      const newerUrl = imageCache.get(key);
      if (newerUrl) return newerUrl;
      imageCache.set(key, signedUrl);
      return signedUrl;
    })
    .finally(() => {
      inFlightSignedUrlRequests.delete(key);
    });

  inFlightSignedUrlRequests.set(key, request);
  return request;
};
